import { appRouting } from 'v5/core/routing/appRouting';

/**
 * The dashboard lives inside a workspace — that is where the sidebar and the
 * rest of the chrome are anchored — so the path carries the workspace and the
 * query string carries what the page does with it: `?team=` narrows to one
 * team, `?workspaces=all` widens to every workspace without leaving the
 * workspace you are standing in.
 */
export interface DashboardFilterParams {
  teamSlug?: string;
  allWorkspaces?: boolean;
  /** Which layout, when a link names one; otherwise the user's own pick. */
  view?: DashboardView;
}

/** Four layouts over the same data, each for a different way of working. */
export const DASHBOARD_VIEWS = ['today', 'plan', 'table', 'overview'] as const;
export type DashboardView = (typeof DASHBOARD_VIEWS)[number];

export const DASHBOARD_TEAM_PARAM = 'team';
export const DASHBOARD_WORKSPACES_PARAM = 'workspaces';
export const DASHBOARD_ALL_WORKSPACES_VALUE = 'all';
export const DASHBOARD_VIEW_PARAM = 'view';

export function dashboardPath(
  organizationId: string,
  workspaceSlug: string,
  filters: DashboardFilterParams = {}
): string {
  const path = appRouting
    .org(organizationId)
    .workspace(workspaceSlug)
    .dashboard();
  const params = new URLSearchParams();

  if (filters.allWorkspaces) {
    params.set(DASHBOARD_WORKSPACES_PARAM, DASHBOARD_ALL_WORKSPACES_VALUE);
  } else if (filters.teamSlug) {
    // Teams belong to a workspace, so they never travel with the wide view.
    params.set(DASHBOARD_TEAM_PARAM, filters.teamSlug);
  }

  if (filters.view) {
    params.set(DASHBOARD_VIEW_PARAM, filters.view);
  }

  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

export function parseDashboardFilters(search: string): DashboardFilterParams {
  const params = new URLSearchParams(search);
  const viewParam = params.get(DASHBOARD_VIEW_PARAM);
  const view = DASHBOARD_VIEWS.find(candidate => candidate === viewParam);
  const withView = (filters: DashboardFilterParams) =>
    view ? { ...filters, view } : filters;

  if (
    params.get(DASHBOARD_WORKSPACES_PARAM) === DASHBOARD_ALL_WORKSPACES_VALUE
  ) {
    return withView({ allWorkspaces: true });
  }

  const teamSlug = params.get(DASHBOARD_TEAM_PARAM) ?? undefined;
  return withView(teamSlug ? { teamSlug } : {});
}
